import React, { useState } from 'react';
import { AttachFile as AttachFileIcon, Send as SendIcon } from '@mui/icons-material';

const ReplySection = ({ onReply, disabled = false, loading = false }) => {
  const [replyText, setReplyText] = useState('');
  const [attachedFiles, setAttachedFiles] = useState([]);

  const handleTextChange = (e) => {
    setReplyText(e.target.value);
  };

  const handleFileChange = (e) => {
    const files = Array.from(e.target.files);
    setAttachedFiles(prev => [...prev, ...files]);
  };

  const handleRemoveFile = (index) => {
    setAttachedFiles(prev => prev.filter((_, i) => i !== index));
  };

  const handleSubmit = () => {
    if (replyText.trim() || attachedFiles.length > 0) {
      onReply(replyText, attachedFiles);
      setReplyText('');
      setAttachedFiles([]);
      // Reset file input
      document.getElementById('reply-file-input').value = '';
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSubmit();
    }
  };

  const canSubmit = !disabled && !loading && (replyText.trim() || attachedFiles.length > 0);

  return (
    <div className="bg-white p-4 rounded-lg shadow-sm border border-gray-100 w-full">
      {/* File attachments preview */}
      {attachedFiles.length > 0 && (
        <div className="flex flex-wrap gap-2 mb-3">
          {attachedFiles.map((file, index) => (
            <div
              key={index}
              className="flex items-center gap-2 bg-gray-50 px-3 py-2 rounded-md border border-gray-200"
            >
              <div className="text-blue-500 text-sm">
                📎
              </div>
              <span className="text-sm text-gray-700 truncate max-w-[150px]">
                {file.name}
              </span>
              <button
                type="button"
                onClick={() => handleRemoveFile(index)}
                className="text-gray-400 hover:text-red-500 transition-colors ml-2"
                disabled={loading}
              >
                ×
              </button>
            </div>
          ))}
        </div>
      )}

      {/* Reply input */}
      <h3 className="text-gray-800 font-bold text-sm mb-3">Reply</h3>
      <div className="flex items-center gap-2 rounded-lg p-1 bg-gray-50 border border-gray-200">
        <label className="p-2 text-gray-400 hover:text-gray-600 transition-colors cursor-pointer">
          <AttachFileIcon fontSize="small" />
          <input
            id="reply-file-input"
            type="file"
            multiple
            className="hidden"
            onChange={handleFileChange}
            disabled={loading}
          />
        </label>

        <input
          type="text"
          placeholder="Type your message here..."
          className={`
            flex-1 bg-transparent border-none outline-none text-sm text-gray-700
            placeholder-gray-400 px-2
            ${disabled ? 'cursor-not-allowed' : ''}
          `}
          value={replyText}
          onChange={handleTextChange}
          onKeyPress={handleKeyPress}
          disabled={disabled || loading}
        />

        <button
          onClick={handleSubmit}
          disabled={!canSubmit}
          className={`
            bg-orange-500 text-white p-2 rounded-md flex items-center justify-center
            transition-colors shadow-sm
            ${canSubmit ? 'hover:bg-orange-600' : 'opacity-50 cursor-not-allowed'}
            ${loading ? 'animate-pulse' : ''}
          `}
        >
          {loading ? (
            <svg className="animate-spin h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12c0 4.477-3.518 8.125-8.046 8.125-3.271 0-6.018-2.412-6.869-5.803l-.375-1.473c-.194-.765-.722-1.41-1.449-1.878C8.509 1.934 7.234 1 5.785 1H4.215C2.506 1 1 2.506 1 4.215v12.524C1 18.494 2.506 20 4.215 20h12.524c1.712 0 3.215-1.506 3.215-3.261v-1.412c0-1.714-1.503-3.215-3.215-3.261z"></path>
            </svg>
          ) : (
            <SendIcon fontSize="small" />
          )}
        </button>
      </div>

      {/* Helper text */}
      <div className="mt-2 text-xs text-gray-400">
        Press Enter to send • Shift+Enter for new line • Maximum file size: 10MB
      </div>
    </div>
  );
};

export default ReplySection;
